'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'next/navigation';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useAuthorization } from 'src/hooks/authorization';
import { Button, Checkbox, Search } from 'src/components';
import {
  useGetCompanyUserQuery,
  useUpdateCompanyUserMutation,
  useGetCompanyManagersQuery,
} from 'src/api/redux';

const ManagersTabContent = () => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const { userManagementCapabilities } = useAuthorization();
  // Manager assignment is a write on the company user (MKT-411).
  const { canWrite } = userManagementCapabilities;

  // Get IDs from route params
  const { companyId, userId } = useParams<{
    companyId: string;
    userId: string;
  }>();

  // Get data from Redux queries
  const { data: currentUser, refetch: mutateUser } = useGetCompanyUserQuery(
    { companyId, userId },
    { skip: !companyId || !userId }
  );

  const { data: managers } = useGetCompanyManagersQuery(
    { companyId },
    { skip: !companyId }
  );
  const [updateCompanyUser] = useUpdateCompanyUserMutation();

  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');

  const currentManagers = useMemo(() => {
    if (!currentUser?.managers) return [];
    return currentUser?.managers.map((manager) => manager.id);
  }, [currentUser]);

  const sortedManagers = useMemo(() => {
    if (!managers?.results) return;
    return [...managers.results]
      .sort((a, b) => {
        // order by first_name
        return a.first_name?.localeCompare(b.first_name);
      })
      .sort((a, b) => {
        // if the user is in the managers, move them to the top
        if (currentManagers?.includes(a.id)) {
          return -1;
        }
        if (currentManagers?.includes(b.id)) {
          return 1;
        }
        return 0;
      });
  }, [currentManagers, managers?.results]);

  const filteredManagers = useMemo(() => {
    if (!sortedManagers) return [];
    return sortedManagers.filter((manager) =>
      `${manager.first_name} ${manager.last_name}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [sortedManagers, search]);

  const methods = useForm({
    values: {
      managers: currentManagers,
    },
  });

  const {
    handleSubmit,
    control,
    getValues,
    reset,
    setValue,
    formState: { isDirty, isValid },
  } = methods;

  // Reset form when currentUser changes
  useEffect(() => {
    reset({ managers: currentManagers });
  }, [currentUser, reset, currentManagers]);

  const handleOnClearManagers = () => {
    setValue('managers', [], { shouldDirty: true });
  };

  const handleCancel = () => {
    reset();
  };

  const onSubmit = async (data) => {
    if (!data || !companyId || !userId) return;
    setIsLoading(true);
    try {
      // unwrap() so RTK Query throws on HTTP errors and the catch below runs. Without it a
      // rejected save resolved normally and still reported success to the user.
      await updateCompanyUser({
        companyId,
        userId,
        ...data,
      }).unwrap();

      log.event('User managers updated', {
        source: 'ManagersTabContent',
        user: userId,
        data: data,
      });

      setNotification({
        title: 'Success',
        message: 'Users managers updated',
        type: 'success',
      });
    } catch (err) {
      log.exception('Error updating user managers', {
        tags: {
          source: 'ManagersTabContent',
          error: err,
        },
      });
      setNotification({
        title: 'Error',
        // unwrap() throws the raw RTK Query error, so the API body is under `data`.
        message: err?.data?.error_message || 'Error updating user managers',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
      reset();
      mutateUser();
    }
  };

  const handleChange = (e, field, user) => {
    const currentUserManagers = getValues('managers');
    field.onChange(
      e.target.checked
        ? [...currentUserManagers, user.id]
        : currentUserManagers.filter((report) => report !== user.id)
    );
  };

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2 pt-4">
          <div className="flex justify-between items-center">
            <Search
              placeholder="Search managers"
              value={search}
              onSearch={(value) => setSearch(value)}
              className="w-full rounded-full max-w-md"
              data-testid="manager-search"
            />
            {canWrite && (
              <Button
                variant="plain"
                color="primary"
                onClick={handleOnClearManagers}
                className="text-sm"
                data-testid="clear-managers-button"
              >
                clear all
              </Button>
            )}
          </div>
          <div
            className="bg-neutral-50 rounded-lg divide-y divide-neutral-200 h-48 overflow-hidden overflow-y-auto border border-neutral-200"
            aria-label="managers"
          >
            {filteredManagers?.map((user, index) => (
              <div
                key={index}
                className="flex items-center justify-between px-3 py-2 first:rounded-t-lg last:rounded-b-lg hover:bg-neutral-100 transition-colors"
              >
                <span className="text-sm text-neutral-700">
                  {`${user.first_name} ${user.last_name}`}
                </span>
                <Controller
                  name="managers"
                  control={control}
                  render={({ field }) => {
                    return (
                      <Checkbox
                        size="sm"
                        color="primary"
                        data-testid={`manager-checkbox-${user.id}`}
                        checked={field.value?.includes(user.id)}
                        onChange={(e) => handleChange(e, field, user)}
                        disabled={!canWrite}
                      />
                    );
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {canWrite && (
          <div className="flex justify-between items-center border-t border-neutral-200 pt-4">
            <Button
              variant="outlined"
              color="neutral"
              onClick={handleCancel}
              disabled={!isDirty}
            >
              Cancel
            </Button>
            <Button
              loading={isLoading}
              variant="solid"
              color="primary"
              onClick={handleSubmit(onSubmit)}
              disabled={!isDirty || !isValid}
            >
              Update
            </Button>
          </div>
        )}
      </div>
    </FormProvider>
  );
};

export default ManagersTabContent;
